import type { PayableRow, VendorPaymentCreate } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, ErrorBox, PageHead, PracticeBanner, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatStamp } from '../format';
import { useDesktop } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

const METHODS = ['check', 'ach', 'zelle', 'cash', 'card', 'other'] as const;

type BillSort = 'vendor' | 'no' | 'date' | 'due' | 'late' | 'total' | 'paid' | 'balance';
const AP_BUCKETS = ['notDue', 'd1to30', 'd31to60', 'd61plus'] as const;
/** Which age a bill sits in: not due yet (or due today), 1-30, 31-60, or 61+ days late. */
export function apBucket(daysLate: number | null): (typeof AP_BUCKETS)[number] {
  if (daysLate === null || daysLate <= 0) return 'notDue';
  if (daysLate <= 30) return 'd1to30';
  if (daysLate <= 60) return 'd31to60';
  return 'd61plus';
}

/** Accounts payable: every vendor invoice with a balance, oldest due first; pay from the row; reverse a mistake. */
export function PayablesPage() {
  const desktop = useDesktop();
  return desktop ? <PayablesSheet /> : <PayablesList />;
}

/** The desk's payables: one sheet of open bills with their age, the AP aging in a strip, pay the selected bill. */
function PayablesSheet() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const payables = useQuery({ queryKey: qk.payables, queryFn: ({ signal }) => api.payables(signal) });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.payables });
    void qc.invalidateQueries({ queryKey: ['vendor-docs'] });
  };
  // the bill being paid, by id: the panel reads the bill from the latest list, so a refresh never leaves it stale
  const [payingId, setPayingId] = useState<string | null>(null);
  const reverse = useMutation({ mutationFn: (id: string) => api.reverseVendorPayment(id, 'reversed from the payables screen'), onSuccess: invalidate });
  const sorters = useMemo(
    () => ({
      vendor: byText<PayableRow>((r) => r.vendorName),
      no: byText<PayableRow>((r) => r.docNumber),
      date: byText<PayableRow>((r) => r.docDate),
      due: byText<PayableRow>((r) => r.dueDate ?? r.docDate),
      late: byNumber<PayableRow>((r) => r.daysLate),
      total: byNumber<PayableRow>((r) => r.total),
      paid: byNumber<PayableRow>((r) => r.paid),
      balance: byNumber<PayableRow>((r) => r.balance),
    }),
    [],
  );
  const { sorted, head } = useSort<PayableRow, BillSort>(sorters, { key: 'due', dir: 1 }, ['late', 'total', 'paid', 'balance']);
  const money = (v: number) => formatMoney(v, intlLocale);
  const status = (r: PayableRow) => (r.daysLate === null || r.daysLate < 0 ? t('notDue') : r.daysLate === 0 ? t('dueToday') : t('daysLateN').replace('{n}', String(r.daysLate)));
  const columns: SheetColumn<PayableRow>[] = [
    { key: 'vendor', header: head('vendor', t('vendor')), label: t('vendor'), width: '180px', render: (r) => r.vendorName },
    { key: 'no', header: head('no', t('billNoCol')), label: t('billNoCol'), width: '110px', render: (r) => r.docNumber ?? '' },
    { key: 'date', header: head('date', t('billDateCol')), label: t('billDateCol'), width: '110px', render: (r) => formatDate(r.docDate, intlLocale, 'date') },
    { key: 'due', header: head('due', t('dueDate')), label: t('dueDate'), width: '110px', render: (r) => (r.dueDate ? formatDate(r.dueDate, intlLocale, 'date') : '') },
    {
      key: 'late',
      header: head('late', t('colStatus')),
      label: t('colStatus'),
      width: '110px',
      render: (r) => <span className={r.daysLate !== null && r.daysLate > 0 ? 'state state-overdue' : r.daysLate === 0 ? 'state state-open' : undefined}>{status(r)}</span>,
    },
    { key: 'total', header: head('total', t('total')), label: t('total'), width: '104px', align: 'right', render: (r) => money(r.total) },
    { key: 'paid', header: head('paid', t('paid')), label: t('paid'), width: '104px', align: 'right', render: (r) => (r.paid ? money(r.paid) : '') },
    { key: 'balance', header: head('balance', t('balance')), label: t('balance'), width: '110px', align: 'right', render: (r) => <strong>{money(r.balance)}</strong>, cellClass: (r) => (r.daysLate !== null && r.daysLate > 0 ? 'bad' : undefined) },
  ];

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('payablesTitle')}</h1>
          <p className="muted">{t('payablesHint')} {t('payHint')}</p>
          <PracticeBanner kind="bills" />
        </div>
        <Link to="/costs/bills" className="btn btn-secondary btn-sm">
          {t('vendorBills')}
        </Link>
      </header>
      <QueryView query={payables}>
        {(p) => {
          const rows = sorted(p.rows);
          const paying = p.rows.find((r) => r.documentId === payingId) ?? null;
          const reversedIds = new Set(p.recent.flatMap((x) => (x.reversesPaymentId ? [x.reversesPaymentId] : [])));
          const byAge = Object.fromEntries(AP_BUCKETS.map((b) => [b, Math.round(p.rows.filter((r) => apBucket(r.daysLate) === b).reduce((s, r) => s + r.balance, 0) * 100) / 100])) as Record<(typeof AP_BUCKETS)[number], number>;
          const footer = (
            <tr>
              <td>{t('reportTotal')}</td>
              <td />
              <td />
              <td />
              <td />
              <td className="al-right">{money(Math.round(p.rows.reduce((s, r) => s + r.total, 0) * 100) / 100)}</td>
              <td className="al-right">{money(Math.round(p.rows.reduce((s, r) => s + r.paid, 0) * 100) / 100)}</td>
              <td className="al-right" data-testid="payables-total">
                <strong>{money(p.totals.open)}</strong>
              </td>
            </tr>
          );
          return (
            <>
              <dl className="totals-strip" data-testid="payables-strip">
                <div>
                  <dt>{t('openBills')}</dt>
                  <dd data-testid="payables-open">{money(p.totals.open)}</dd>
                </div>
                <div className={p.totals.overdue > 0 ? 'bad' : undefined}>
                  <dt>{t('overdueTotal')}</dt>
                  <dd>{money(p.totals.overdue)}</dd>
                </div>
                <div>
                  <dt>{t('dueThisWeek')}</dt>
                  <dd>{money(p.totals.dueThisWeek)}</dd>
                </div>
                <div>
                  <dt>{t('notDue')}</dt>
                  <dd>{money(byAge.notDue)}</dd>
                </div>
                <div>
                  <dt>{t('bucket_d1to30')}</dt>
                  <dd>{money(byAge.d1to30)}</dd>
                </div>
                <div>
                  <dt>{t('bucket_d31to60')}</dt>
                  <dd>{money(byAge.d31to60)}</dd>
                </div>
                <div className={byAge.d61plus > 0 ? 'bad' : undefined}>
                  <dt>61+</dt>
                  <dd>{money(byAge.d61plus)}</dd>
                </div>
                <div>
                  <dt>{t('vendorsCol')}</dt>
                  <dd>{p.totals.vendors}</dd>
                </div>
              </dl>
              {paying ? (
                <section className="card pay-panel" aria-label={t('payBillTitle').replace('{vendor}', paying.vendorName).replace('{no}', paying.docNumber ?? '')}>
                  <div className="row-title">
                    {t('payBillTitle').replace('{vendor}', paying.vendorName).replace('{no}', paying.docNumber ?? '')} · {t('balance')} {money(paying.balance)}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPayingId(null)}>
                      {t('close')}
                    </button>
                  </div>
                  <PayForm
                    key={`${paying.documentId}-${paying.balance}`}
                    row={paying}
                    onDone={() => {
                      setPayingId(null);
                      invalidate();
                    }}
                  />
                </section>
              ) : null}
              {rows.length === 0 ? (
                <p className="muted pick-first">{t('noBills')}</p>
              ) : (
                <Sheet label={t('payablesTitle')} columns={columns} rows={rows} rowKey={(r) => r.documentId} rowLabel={(r) => `${r.vendorName} ${r.docNumber ?? ''}`} stickyFirst minWidth="940px" footer={footer} onOpen={(r) => setPayingId(r.documentId)} />
              )}
              {p.recent.length > 0 ? (
                <section className="stack-sm">
                  <h2>{t('recentPayments')}</h2>
                  <div className="table-wrap">
                    <table className="data-table" data-testid="recent-payments">
                      <thead>
                        <tr>
                          <th scope="col">{t('docDate')}</th>
                          <th scope="col">{t('vendor')}</th>
                          <th scope="col">{t('billNoCol')}</th>
                          <th scope="col">{t('method')}</th>
                          <th scope="col">{t('reference')}</th>
                          <th scope="col" className="num">{t('amount')}</th>
                          <th scope="col" />
                        </tr>
                      </thead>
                      <tbody>
                        {p.recent.map((x) => (
                          <tr key={x.id}>
                            <td>{formatStamp(x.paidAt, intlLocale, 'date')}</td>
                            <td>{x.vendorName}</td>
                            <td>{x.docNumber ?? ''}</td>
                            <td>{t(`method_${x.method}`)}</td>
                            <td>
                              {x.reference ?? ''}
                              {x.reversesPaymentId ? ` · ${t('reversed')}` : ''}
                            </td>
                            <td className="num">{money(x.amount)}</td>
                            <td>
                              {x.amount > 0 && !x.reversesPaymentId && !reversedIds.has(x.id) ? (
                                <button type="button" className="btn btn-ghost btn-sm" disabled={reverse.isPending} onClick={() => window.confirm(t('confirmReverse').replace('{amount}', money(x.amount)).replace('{vendor}', x.vendorName)) && reverse.mutate(x.id)} aria-label={`${t('reverse')} ${x.vendorName} ${money(x.amount)}`}>
                                  {t('reverse')}
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {reverse.isError ? <ErrorBox error={reverse.error} /> : null}
                </section>
              ) : null}
            </>
          );
        }}
      </QueryView>
    </div>
  );
}

/** The phone's payables: a card per bill, tap to pay. */
function PayablesList() {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const payables = useQuery({ queryKey: qk.payables, queryFn: ({ signal }) => api.payables(signal) });
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.payables });
    void qc.invalidateQueries({ queryKey: ['vendor-docs'] });
  };
  const [open, setOpen] = useState<string | null>(null);
  const reverse = useMutation({ mutationFn: (id: string) => api.reverseVendorPayment(id, 'reversed from the bills screen'), onSuccess: invalidate });
  return (
    <div className="stack">
      <PageHead title={t('payablesTitle')} back="/" />
      <PracticeBanner kind="bills" />
      <p className="muted">{t('payablesHint')}</p>
      <QueryView query={payables}>
        {(p) => (
          <>
            <section className="card">
              <dl className="facts plain">
                <div className="fact">
                  <dt>{t('openBills')}</dt>
                  <dd className="money" data-testid="payables-open">{formatMoney(p.totals.open, intlLocale)}</dd>
                </div>
                <div className="fact">
                  <dt>{t('overdue')}</dt>
                  <dd className={p.totals.overdue > 0 ? 'money owed' : 'money'}>{formatMoney(p.totals.overdue, intlLocale)}</dd>
                </div>
                <div className="fact">
                  <dt>{t('dueThisWeek')}</dt>
                  <dd className="money">{formatMoney(p.totals.dueThisWeek, intlLocale)}</dd>
                </div>
              </dl>
            </section>
            {p.rows.length === 0 ? (
              <Empty>{t('noBills')}</Empty>
            ) : (
              <ul className="list" data-testid="payables-rows">
                {p.rows.map((r) => (
                  <li key={r.documentId} className="card stack-sm" data-testid={`bill-${r.documentId}`}>
                    <button type="button" className="row" onClick={() => setOpen(open === r.documentId ? null : r.documentId)} aria-expanded={open === r.documentId}>
                      <span className="row-main">
                        <span className="row-title">
                          {r.vendorName} {r.docNumber ? <span className="label">{r.docNumber}</span> : null}
                          {r.daysLate !== null && r.daysLate > 0 ? <span className="badge badge-danger">{r.daysLate} {t('daysLateShort')}</span> : null}
                        </span>
                        <span className="row-sub">
                          {formatDate(r.docDate, intlLocale)}
                          {r.dueDate ? ` · ${t('dueDate')} ${formatDate(r.dueDate, intlLocale)}` : ''}
                          {r.paid > 0 ? ` · ${t('paid')} ${formatMoney(r.paid, intlLocale)} / ${formatMoney(r.total, intlLocale)}` : ''}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className={r.daysLate !== null && r.daysLate > 0 ? 'row-end-value owed' : 'row-end-value'}>{formatMoney(r.balance, intlLocale)}</span>
                      </span>
                    </button>
                    {open === r.documentId ? (
                      <PayForm row={r} onDone={() => { setOpen(null); invalidate(); }} />
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {p.recent.length > 0 ? (
              <section className="card stack-sm">
                <h2>{t('recentPayments')}</h2>
                <ul className="list plain">
                  {p.recent.map((x) => (
                    <li key={x.id} className="row">
                      <span className="row-main">
                        <span className="row-title">
                          {x.vendorName} {x.docNumber ? <span className="label">{x.docNumber}</span> : null}
                        </span>
                        <span className="row-sub">
                          {formatStamp(x.paidAt, intlLocale)} · {t(`method_${x.method}`)}{x.reference ? ` ${x.reference}` : ''}{x.reversesPaymentId ? ` · ${t('reversed')}` : ''}
                        </span>
                      </span>
                      <span className="row-end">
                        <span className="row-end-value">{formatMoney(x.amount, intlLocale)}</span>
                        {x.amount > 0 && !x.reversesPaymentId ? (
                          <button type="button" className="btn btn-ghost btn-sm" disabled={reverse.isPending} onClick={() => { if (window.confirm(`${t('reverse')} ${x.vendorName} ${formatMoney(x.amount, intlLocale)}: ${t('confirmSure')}`)) reverse.mutate(x.id); }} aria-label={`${t('reverse')} ${x.vendorName} ${formatMoney(x.amount, intlLocale)}`}>
                            {t('reverse')}
                          </button>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
                {reverse.isError ? <ErrorBox error={reverse.error} /> : null}
              </section>
            ) : null}
            <Link to="/costs/bills" className="btn btn-ghost btn-block">
              {t('vendorBills')}
            </Link>
          </>
        )}
      </QueryView>
    </div>
  );
}

function PayForm({ row, onDone }: { row: PayableRow; onDone: () => void }) {
  const { t } = useLocale();
  const [amount, setAmount] = useState(String(row.balance));
  const [method, setMethod] = useState<VendorPaymentCreate['method']>('check');
  const [reference, setReference] = useState('');
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const pay = useMutation({
    mutationFn: (v: { body: VendorPaymentCreate; key: string }) => api.payVendor(v.body, v.key),
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: () => {
      attempt.current = null;
      onDone();
    },
  });
  const n = parseNumber(amount);
  const valid = n !== null && n > 0 && n <= row.balance + 0.004;
  const submit = () => {
    if (!valid) return;
    const body: VendorPaymentCreate = { vendorId: row.vendorId, documentId: row.documentId, amount: Math.round(n * 100) / 100, method, ...(reference.trim() ? { reference: reference.trim() } : {}) };
    const serialized = JSON.stringify(body);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    pay.mutate({ body, key: attempt.current.key });
  };
  return (
    <div className="stack-sm" data-testid="pay-form">
      <div className="contact-actions">
        <label className="field" style={{ flex: 1 }}>
          <span>{t('amount')}</span>
          <input className="count-input" type="text" inputMode="decimal" autoComplete="off" aria-label={`${t('amount')} ${row.vendorName}`} value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('method')}</span>
          <select aria-label={`${t('method')} ${row.vendorName}`} value={method} onChange={(e) => setMethod(e.target.value as VendorPaymentCreate['method'])}>
            {METHODS.map((m) => (
              <option key={m} value={m}>
                {t(`method_${m}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('reference')}</span>
          <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} autoComplete="off" aria-label={`${t('reference')} ${row.vendorName}`} style={{ width: 110 }} />
        </label>
      </div>
      {pay.isError ? <ErrorBox error={pay.error} /> : null}
      <button type="button" className="btn btn-primary btn-block" disabled={!valid || pay.isPending} onClick={submit}>
        {t('pay')} {valid ? formatMoneyPlain(n) : ''}
      </button>
    </div>
  );
}

const formatMoneyPlain = (n: number) => `$${n.toFixed(2)}`;
